import React, { useCallback, useMemo, useState, useEffect } from 'react';
import { RouteComponentProps } from 'react-router-dom';

import * as Yup from 'yup';
import FmcForm from 'components/FmcForm';
import EligibilityView from './EligibilityView';

import { isBornAfter, isBeforeToday, isSsn } from 'utils/validations';
import useWorkflow, { Step } from 'hooks/useWorkflow';
import useNavigationHelpers from 'hooks/useNavigationHelpers';
import useTranslate from 'hooks/useTranslate';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { useSnackbar } from 'notistack';
import {
  mergeApplication,
  selectCreditApplication,
} from 'state/creditApplication';
import { pick, any, propEq, find, filter } from 'ramda';
import catchOnly from 'utils/catchOnly';
import { NonClientError } from 'hooks/requests/fetchUtils';
import useUnifiedBuyer, {
  UnifiedBuyerError,
  UnifiedBuyerParams,
} from 'hooks/requests/unifiedBuyer';
import useUnifiedOffers, {
  UnifiedOffersError,
  UnifiedOffersResult,
  PartialError,
} from 'hooks/requests/unifiedOffers';
import useFieldCustomization from 'hooks/useFieldCustomization';
import { selectTenant, isOfferConfigured, Bread } from 'state/tenant';
import useAnalytics, { Event } from 'hooks/useAnalytics';
import { selectAnalytics, setAnalyticsState } from 'state/analytics';
import useAnalyticsVariables, {
  OfferWidgetType,
} from 'hooks/useAnalyticVariables';
import { selectExpandedPanel } from 'state/unifiedWidgets';

type PropTypes = RouteComponentProps;

export interface FormValues {
  dateOfBirth: string | null;
  ssn: string | null;
}

const fields = ['dateOfBirth', 'ssn'];

const isError = (
  prequal:
    | UnifiedOffersResult['prequalification']
    | UnifiedOffersResult['financing'],
): prequal is PartialError =>
  typeof prequal !== 'undefined' && 'error' in prequal;

type WidgetState = Partial<Record<OfferWidgetType, boolean>>;

const useSetAnalyticsFromResponse = () => {
  const { setAnalyticsVariables } = useAnalyticsVariables();
  const expandedPanel = useSelector(selectExpandedPanel);

  const hasExpandedPanel = useCallback(
    (widgetState: WidgetState): OfferWidgetType | null => {
      if (expandedPanel === 'prequalification' && widgetState.prequalOffer) {
        return 'prequalOffer';
      } else if (expandedPanel === 'installments' && widgetState.installments) {
        return 'installments';
      } else if (expandedPanel === 'splitpay' && widgetState.installments) {
        return 'splitpay';
      } else {
        return null;
      }
    },
    [expandedPanel],
  );

  const widgetStateFromResponse = useCallback(
    (results: UnifiedOffersResult, bread: Bread): WidgetState => {
      const prequalOffer = results.prequalification;
      const financingOffers = results.financing;
      const widgetState = {
        installments: false,
        splitpay: false,
        prequalOffer: false,
        prequalAccountFound: false,
        notQualified: false,
      };

      if (
        isOfferConfigured('prequalification', bread) &&
        prequalOffer &&
        !isError(prequalOffer)
      ) {
        if (prequalOffer.hasExistingAccount) {
          widgetState.prequalAccountFound = true;
        } else if (prequalOffer.prequalificationId && prequalOffer.hasOffer) {
          widgetState.prequalOffer = true;
        } else if (prequalOffer.notQualified) {
          widgetState.notQualified = true;
        }
      }

      const hasOffer = propEq('paymentProductType');

      if (
        isOfferConfigured('installments', bread) &&
        financingOffers &&
        !isError(financingOffers) &&
        any(hasOffer('INSTALLMENTS'), financingOffers.offers || [])
      ) {
        widgetState.installments = true;
      }

      if (
        isOfferConfigured('splitpay', bread) &&
        financingOffers &&
        !isError(financingOffers) &&
        any(hasOffer('SPLITPAY'), financingOffers.offers || [])
      ) {
        widgetState.splitpay = true;
      }
      return widgetState;
    },
    [],
  );

  const findOfferOnTop = useCallback(
    (widgetState: WidgetState) => {
      if (widgetState.prequalAccountFound) {
        return 'prequalAccountFound';
      } else {
        const expandedPanel = hasExpandedPanel(widgetState);
        if (expandedPanel) {
          return expandedPanel;
        } else if (
          widgetState.prequalOffer ||
          widgetState.installments ||
          widgetState.splitpay
        ) {
          // Find the first shown widget
          const order = [
            'installments',
            'splitpay',
            'prequalOffer',
          ] as (keyof WidgetState)[];
          return find(key => Boolean(widgetState[key]), order);
        } else if (widgetState.notQualified) {
          return 'notQualified';
        }
      }
    },
    [hasExpandedPanel],
  );

  return useCallback(
    (results: UnifiedOffersResult, bread: Bread) => {
      const widgetState = widgetStateFromResponse(results, bread);

      const result = filter(key => Boolean(widgetState[key]), Object.keys(
        widgetState,
      ) as OfferWidgetType[]);

      setAnalyticsVariables({
        offersShown: result.join(','),
        offerOnTop: findOfferOnTop(widgetState),
      });
    },
    [findOfferOnTop, setAnalyticsVariables, widgetStateFromResponse],
  );
};

const Eligibility: React.FC<PropTypes> = () => {
  const { bread } = useSelector(selectTenant);
  const { pathAfter } = useWorkflow();
  if (!bread) {
    throw new Error('Invalid tenant');
  }
  const dispatch = useDispatch();
  const { push } = useNavigationHelpers();
  const { enqueueSnackbar } = useSnackbar();
  const [unifiedBuyer] = useUnifiedBuyer();
  const [unifiedOffers] = useUnifiedOffers();
  const store = useStore();
  const [navigateToNextStep, setNavigateToNextStep] = useState(false);
  const { isLastFour } = useFieldCustomization();

  const application = useSelector(selectCreditApplication);
  const { existingAccountBehavior } = useSelector(selectTenant);
  const initialValues: FormValues = pick(fields, application);
  const isLastFourBool = isLastFour();
  const { sendEvents } = useAnalytics();
  const { prequalRequestCompleted } = useSelector(selectAnalytics);

  const setAnalyticsFromResponse = useSetAnalyticsFromResponse();

  const t = useTranslate();
  const schema = useMemo(() => {
    return Yup.object().shape({
      dateOfBirth: Yup.string()
        .label(t('formLabel.dateOfBirth'))
        .required()
        .nullable()
        .concat(isBeforeToday())
        .concat(isBornAfter(1900)),
      ssn: Yup.string()
        .label(isLastFourBool ? 'Last 4 SSN' : 'SSN')
        .nullable()
        .concat(isSsn(isLastFourBool))
        .required(),
    });
  }, [t, isLastFourBool]);

  const onSubmit = useCallback(
    async (values: FormValues) => {
      if (!prequalRequestCompleted) {
        sendEvents([Event.PrequalRequestCompleted]);
        dispatch(setAnalyticsState({ prequalRequestCompleted: true }));
      }
      const castValues = schema.cast(values);

      const {
        mobilePhone,
        emailAddress,
        address1,
        address2,
        city,
        state,
        zip,
        firstName,
        lastName,
        middleInitial,
      } = application;

      try {
        dispatch(mergeApplication(castValues));

        const buyerParams = {
          ssn: castValues.ssn,
          dateOfBirth: castValues.dateOfBirth,

          mobilePhone,
          emailAddress,
          address1,
          address2,
          city,
          state,
          zip,
          firstName,
          lastName,
          middleInitial,
        } as UnifiedBuyerParams;

        await unifiedBuyer(buyerParams);

        // Use store.getState to get the latest creditApplication value
        // after dispatch
        const applicationParams = selectCreditApplication(store.getState());
        const response = await unifiedOffers(applicationParams);

        const { prequalification: prequalOffer, financing } = response;

        setAnalyticsFromResponse(response, bread);

        if (
          financing &&
          !isError(financing) &&
          financing.status === 'APPROVED'
        ) {
          // If there's a Bread offer, always show the Offer page
          setNavigateToNextStep(true);
        } else if (prequalOffer && !isError(prequalOffer)) {
          if (
            prequalOffer.hasExistingAccount &&
            existingAccountBehavior === 'noDsp'
          ) {
            // Let ExistingAccount handle this case, as there
            push('/existing-account');
          } else if (
            prequalOffer.hasExistingAccount ||
            prequalOffer.hasOffer ||
            prequalOffer.notQualified
          ) {
            // If there's an existing account or offer, show the Offer
            // page with its widgets
            setNavigateToNextStep(true);
          } else {
            // If ADS not "successful" and Bread not "approved", go to generic
            // pending screen
            push('/unified/offer-application/pending');
          }
        } else if (
          financing &&
          !isError(financing) &&
          financing.status !== 'APPROVED'
        ) {
          // We have financing offer but not approved.
          // Go to generic screen.
          push('/unified/offer-application/pending');
        } else {
          push('/unified/offer-application/error');
        }
      } catch (e) {
        if (
          catchOnly(e, NonClientError, UnifiedBuyerError, UnifiedOffersError)
        ) {
          if (
            e instanceof NonClientError ||
            e instanceof UnifiedBuyerError ||
            e instanceof UnifiedOffersError
          ) {
            push('/unified/offer-application/error');
          } else {
            enqueueSnackbar(
              'Something has gone wrong. Please try again later.',
              {
                variant: 'error',
                persist: true,
              },
            );
          }
        }
      }
    },
    [
      prequalRequestCompleted,
      schema,
      application,
      sendEvents,
      dispatch,
      unifiedBuyer,
      store,
      unifiedOffers,
      setAnalyticsFromResponse,
      bread,
      existingAccountBehavior,
      push,
      enqueueSnackbar,
    ],
  );

  useEffect(() => {
    if (!navigateToNextStep) return;
    // The next step depends on result of API call. Because of that, we need to
    // wait for next render call for 'pathAfter' hook to have chance to receive
    // updated data.
    push(pathAfter(Step.UNIFIED_OFFER_APPLICATION));
  }, [navigateToNextStep, pathAfter, push]);

  return (
    <FmcForm
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={onSubmit}
    >
      {formProps => <EligibilityView {...formProps} />}
    </FmcForm>
  );
};

export default Eligibility;
